/**
 * API Service
 * Requirements: 14.2
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { apiConfig, isLocalMode } from '../config/amplify';
import type {
  Resume,
  ResumeJSON,
  Recommendation,
  UserJob,
  JobListResponse,
  ResumeListResponse,
  GenerationStatusResponse,
  UserPreferences,
  SubcomponentType,
  GenerationType,
  JobPhase,
  ImportUrlResponse,
  ImportProcessResponse,
  JoinOrganizationResponse,
} from '../types';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use(async (config) => {
      if (isLocalMode) {
        // Local mode: send user identity via headers instead of JWT
        const userId = localStorage.getItem('local_user_id') || '';
        const userEmail = localStorage.getItem('local_user_email') || '';
        if (userId) {
          config.headers['X-Local-User-Id'] = userId;
          config.headers['X-Local-User-Email'] = userEmail;
        }
        return config;
      }
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
      return config;
    });

    // Handle 401 (invalid/expired token) - sign out and redirect to login
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (isLocalMode) return Promise.reject(error);
        if (error.response?.status === 401) {
          try {
            await signOut();
          } catch {
            // Clear any remaining auth state manually
            localStorage.clear();
            sessionStorage.clear();
          }
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  // Resume endpoints
  async listResumes(): Promise<ResumeListResponse> {
    const response = await this.client.get('/resumes');
    return response.data;
  }

  async getResume(resumename: string): Promise<Resume> {
    const response = await this.client.get(`/resumes/${encodeURIComponent(resumename)}`);
    return response.data.resume;
  }

  async createResume(resumename: string, resumejson: ResumeJSON, masterResumePath?: string, publishMasterResume?: boolean): Promise<Resume> {
    const response = await this.client.post('/resumes', { resumename, resumejson, master_resume_path: masterResumePath, publish_master_resume: publishMasterResume });
    return response.data.resume;
  }

  async updateResume(resumename: string, resumejson: ResumeJSON, newresumename?: string, masterResumePath?: string, publishMasterResume?: boolean): Promise<Resume> {
    const body: Record<string, unknown> = { resumejson };
    if (newresumename && newresumename !== resumename) {
      body.newresumename = newresumename;
    }
    if (masterResumePath !== undefined) {
      body.master_resume_path = masterResumePath;
    }
    if (publishMasterResume !== undefined) {
      body.publish_master_resume = publishMasterResume;
    }
    const response = await this.client.put(`/resumes/${encodeURIComponent(resumename)}`, body);
    return response.data.resume;
  }

  async deleteResume(resumename: string): Promise<void> {
    await this.client.delete(`/resumes/${encodeURIComponent(resumename)}`);
  }

  async reviewResume(resumename: string): Promise<{ recommendations: Recommendation[] }> {
    const response = await this.client.post(`/resumes/${encodeURIComponent(resumename)}/review`);
    return response.data;
  }

  // Job endpoints
  async listJobs(phase?: JobPhase, filter?: 'active' | 'all'): Promise<JobListResponse> {
    const params = new URLSearchParams();
    if (phase) params.append('phase', phase);
    if (filter) params.append('filter', filter);
    const response = await this.client.get(`/jobs?${params.toString()}`);
    return response.data;
  }

  async getJob(jobid: string): Promise<UserJob> {
    const response = await this.client.get(`/jobs/${jobid}`);
    return response.data.job;
  }

  async createJobManual(data: {
    jobcompany: string;
    jobtitle: string;
    jobdesc: string;
    resumeid?: string;
    joblocation?: string;
    jobsalary?: string;
    jobposteddate?: string;
    joburl?: string;
    jobtags?: string[];
  }): Promise<UserJob> {
    const response = await this.client.post('/jobs/manual', data);
    return response.data;
  }

  async createJobFromUrl(url: string, resumeid?: string): Promise<UserJob> {
    const response = await this.client.post('/jobs/url', { url, resumeid });
    return response.data;
  }

  async updateJobPhase(jobid: string, phase: JobPhase): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/phase`, { phase });
    return response.data.userJob;
  }

  async deleteJob(jobid: string): Promise<void> {
    await this.client.delete(`/jobs/${jobid}`);
  }

  async updateJobDescription(jobid: string, jobdesc: string): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, { jobdesc });
    return response.data.job;
  }

  async updateJobDetails(
    jobid: string,
    updates: {
      jobdesc?: string;
      jobtitle?: string;
      jobcompany?: string;
      joblocation?: string;
      jobsalary?: string;
      jobtags?: string[];
      jobreqid?: string;
    }
  ): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, updates);
    return response.data.job;
  }

  async submitFeedback(jobid: string, topic: 'resume' | 'coverletter', result: 'U' | 'D'): Promise<{ message: string }> {
    const response = await this.client.post(`/jobs/${jobid}/feedback`, { feedback_topic: topic, result });
    return response.data;
  }

  async updateJobTags(jobid: string, jobtags: string[]): Promise<UserJob> {
    const response = await this.client.put(`/jobs/${jobid}/description`, { jobtags });
    return response.data.job;
  }

  // Generation endpoints
  async generateAll(jobid: string, components?: SubcomponentType[], resumeid?: string): Promise<{ message: string; status: string }> {
    const body: Record<string, SubcomponentType[] | string> = {};
    if (components) body.components = components;
    if (resumeid) body.resumeid = resumeid;
    const response = await this.client.post(`/jobs/${jobid}/generate-all`, Object.keys(body).length > 0 ? body : undefined);
    return response.data;
  }

  async generateSingle(jobid: string, component: SubcomponentType, resumeid?: string): Promise<{ message: string; status: string }> {
    const body = resumeid ? { resumeid } : undefined;
    const response = await this.client.post(`/jobs/${jobid}/generate/${component}`, body);
    return response.data;
  }

  async getGenerationStatus(jobid: string): Promise<GenerationStatusResponse> {
    const response = await this.client.get(`/jobs/${jobid}/status`);
    return response.data;
  }

  async toggleGenerationType(jobid: string, component: SubcomponentType, type: GenerationType): Promise<void> {
    await this.client.put(`/jobs/${jobid}/type/${component}`, { type });
  }

  async updateSubcomponentContent(jobid: string, component: SubcomponentType, content: string): Promise<void> {
    await this.client.put(`/jobs/${jobid}/subcomponent/${component}`, { content });
  }

  // Final file endpoints
  async generateFinalHtml(jobid: string, type: 'resume' | 'cover'): Promise<{ s3Uri: string; publicUrl: string }> {
    const endpoint = type === 'resume' ? 'resume-html' : 'cover-html';
    const response = await this.client.post(`/jobs/${jobid}/final/${endpoint}?type=${type}`);
    return response.data;
  }

  async generateFinalPdf(jobid: string, type: 'resume' | 'cover'): Promise<{ s3Uri: string; publicUrl: string }> {
    const endpoint = type === 'resume' ? 'resume-pdf' : 'cover-pdf';
    const response = await this.client.post(`/jobs/${jobid}/final/${endpoint}?type=${type}`);
    return response.data;
  }

  async generateFinalFiles(jobid: string, type?: 'resume' | 'cover'): Promise<GenerateFinalFilesResponse> {
    const url = type ? `/jobs/${jobid}/generate-final?type=${type}` : `/jobs/${jobid}/generate-final`;
    const response = await this.client.post(url);
    return response.data;
  }

  // Preferences endpoints
  async getPreferences(): Promise<UserPreferences> {
    const response = await this.client.get('/preferences');
    return response.data.preferences;
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    await this.client.put('/preferences', { preferences });
  }

  // Forwarding emails endpoints
  async getForwardingEmails(): Promise<{ forwarding_emails: Array<{ email: string; added_at: string }>; pending_email: string | null; max_emails: number }> {
    const response = await this.client.get('/forwarding-emails');
    return response.data;
  }

  async addForwardingEmail(email: string): Promise<{ message: string; pending_email: string }> {
    const response = await this.client.post('/forwarding-emails', { email, action: 'add' });
    return response.data;
  }

  async verifyForwardingEmail(code: string): Promise<{ message: string; forwarding_emails: Array<{ email: string; added_at: string }> }> {
    const response = await this.client.post('/forwarding-emails', { action: 'verify', code });
    return response.data;
  }

  async resendForwardingCode(): Promise<{ message: string }> {
    const response = await this.client.post('/forwarding-emails', { action: 'resend' });
    return response.data;
  }

  async removeForwardingEmail(email: string): Promise<{ message: string; forwarding_emails: Array<{ email: string; added_at: string }> }> {
    const response = await this.client.delete('/forwarding-emails', { data: { email } });
    return response.data;
  }

  // Resume Import endpoints
  async getImportUrl(filename: string, contentType: string): Promise<ImportUrlResponse> {
    const response = await this.client.post('/resumes/import/url', { filename, contentType });
    return response.data;
  }

  async processImport(s3Key: string): Promise<{ processId: string }> {
    const response = await this.client.post('/resumes/import/process', { s3Key });
    return response.data;
  }

  async getImportStatus(processId: string): Promise<ImportProcessResponse & { status: string; message?: string }> {
    const response = await this.client.get('/resumes/import/status', { params: { processId } });
    return response.data;
  }

  // Admin endpoints
  async getAdminMetrics(): Promise<AdminMetricsResponse> {
    const response = await this.client.get('/admin/metrics');
    return response.data.metrics;
  }

  // Authorization check endpoint
  async checkAuthorization(): Promise<{ authorization: Record<string, unknown>; counts: { jobs: number; resumes: number } }> {
    const response = await this.client.get('/auth/check');
    return response.data;
  }

  // Organization endpoints
  async joinOrganization(code: string): Promise<JoinOrganizationResponse> {
    const response = await this.client.post('/organization/join', { code });
    return response.data;
  }

  // Stripe endpoints
  async createCheckoutSession(): Promise<{ checkoutUrl: string; sessionId: string; lifetime?: boolean; free_plan?: boolean; message?: string }> {
    const response = await this.client.post('/stripe/checkout');
    return response.data;
  }

  async createSubscription(priceTier: 'standard' | 'snooze' = 'standard'): Promise<{
    clientSecret?: string;
    subscriptionId?: string;
    free_plan?: boolean;
    tier_changed?: boolean;
    price_tier?: string;
    message?: string;
  }> {
    const response = await this.client.post('/stripe/subscribe', { price_tier: priceTier });
    return response.data;
  }

  async getCustomerPortalUrl(): Promise<{ portalUrl: string }> {
    const response = await this.client.post('/stripe/portal');
    return response.data;
  }

  // Admin - User Management
  async listUsers(search?: string, limit?: number): Promise<{ users: AdminUserListItem[]; count: number; total: number }> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit) params.append('limit', limit.toString());
    const response = await this.client.get(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getUser(userid: string): Promise<AdminUserDetail> {
    const response = await this.client.get(`/admin/users/${userid}`);
    return response.data;
  }

  async updateUser(userid: string, updates: Record<string, string>): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/users/${userid}`, updates);
    return response.data;
  }

  async getAiMetrics(): Promise<AiMetricsResponse> {
    const response = await this.client.get('/admin/ai-metrics');
    return response.data;
  }

  async getAiUsageData(months: number = 1): Promise<AiUsageResponse> {
    const response = await this.client.get('/admin/ai-usage-data', { params: { months } });
    return response.data;
  }

  // Admin - Marketing
  async getMarketingData(): Promise<{ campaigns: MarketingCampaignData[] }> {
    const response = await this.client.get('/admin/marketing');
    return response.data;
  }

  async createMarketingItem(item: MarketingItemPayload): Promise<{ message: string }> {
    const response = await this.client.post('/admin/marketing', item);
    return response.data;
  }

  async updateMarketingItem(itemId: string, updates: Record<string, string>): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/marketing/${itemId}`, updates);
    return response.data;
  }

  async deleteMarketingItem(itemId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/marketing/${itemId}`);
    return response.data;
  }

  async seedMarketingData(data: { campaigns: unknown[] }): Promise<{ message: string }> {
    const response = await this.client.post('/admin/marketing/seed', data);
    return response.data;
  }

  // Admin - AI Model Management
  async listModels(): Promise<{ models: Array<{ model_id: string; display_name: string; enabled: boolean; model_type?: string }> }> {
    const response = await this.client.get('/admin/models');
    return response.data;
  }

  async updateModel(modelId: string, data: Record<string, string | boolean | number | null>): Promise<void> {
    await this.client.put(`/admin/models/${encodeURIComponent(modelId)}`, data);
  }

  async deleteModel(modelId: string): Promise<void> {
    await this.client.delete(`/admin/models/${encodeURIComponent(modelId)}`);
  }

  async testModel(modelId: string, testType?: string): Promise<{ response: string }> {
    const body: Record<string, string> = { model_id: modelId };
    if (testType) body.test_type = testType;
    const response = await this.client.post('/admin/models/test', body);
    return response.data;
  }

  async seedModels(): Promise<void> {
    await this.client.post('/admin/models/seed');
  }

  async searchBedrockModels(query: string): Promise<{ matches: Array<{ model_id: string; model_name: string; provider: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>; total: number }> {
    const response = await this.client.post('/admin/models/search', { query });
    return response.data;
  }

  async getModelPricing(refresh?: boolean): Promise<{
    pricing: Record<string, { model_id: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>;
    cached: boolean;
    unmatched_models?: Array<{ model_id: string; display_name: string }>;
    available_pricing_names?: string[];
  }> {
    const url = refresh ? '/admin/models/pricing?refresh=true' : '/admin/models/pricing';
    const response = await this.client.get(url);
    return response.data;
  }

  async listModelAssignments(): Promise<{ functions: ModelAssignmentRecord[]; total: number }> {
    const response = await this.client.get('/admin/model-assignments');
    return response.data;
  }

  async updateModelAssignment(functionId: string, data: Record<string, unknown>): Promise<void> {
    await this.client.put(`/admin/model-assignments/${encodeURIComponent(functionId)}`, data);
  }

  async getPostProcessors(): Promise<{ processors: Array<{ name: string; description: string; params: Record<string, { type: string; required: boolean; default?: unknown; description: string }> }> }> {
    const response = await this.client.get('/admin/post-processors');
    return response.data;
  }

  // Admin - Work Queue
  async listWorkQueue(): Promise<{ items: WorkQueueItem[]; total: number }> {
    const response = await this.client.get('/admin/work-queue');
    return response.data;
  }

  async createWorkQueueItem(data: { target_area: string; target_component: string; request_text: string; status?: string }): Promise<{ message: string }> {
    const response = await this.client.post('/admin/work-queue', data);
    return response.data;
  }

  async updateWorkQueueItem(requestId: string, updates: { status?: string; result_notes?: string; target_area?: string; target_component?: string; request_text?: string }): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/work-queue/${requestId}`, updates);
    return response.data;
  }

  async deleteWorkQueueItem(requestId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/work-queue/${requestId}`);
    return response.data;
  }

  async getWorkQueueSettings(): Promise<{ settings: { autofix_risk_threshold: number; error_monitor_interval?: number; error_monitor_expression?: string; error_monitor_error?: string } }> {
    const response = await this.client.get('/admin/work-queue/settings');
    return response.data;
  }

  async updateWorkQueueSettings(settings: { autofix_risk_threshold?: number; error_monitor_interval?: number }): Promise<{ message: string; settings: { autofix_risk_threshold: number; error_monitor_interval?: number }; eventbridge_error?: string }> {
    const response = await this.client.put('/admin/work-queue/settings', settings);
    return response.data;
  }

  // Admin - PDF Calibration
  async getPdfCalibration(): Promise<{ constants: Record<string, number>; defaults: Record<string, number>; overrides: Record<string, number> }> {
    const response = await this.client.get('/admin/pdf-calibration');
    return response.data;
  }

  async updatePdfCalibration(overrides: Record<string, number>): Promise<{ message: string }> {
    const response = await this.client.put('/admin/pdf-calibration', { overrides });
    return response.data;
  }

  async simulatePdfLayout(params: { url?: string; data?: Record<string, unknown>; overrides?: Record<string, number> }): Promise<{
    total_pages: number;
    total_content_height_in: number;
    usable_height_per_page_in: number;
    pages: Array<{ page_num: number; used_height_in: number; available_height_in: number; whitespace_in: number; utilization_pct: number; block_count: number }>;
    blocks: Array<{ type: string; section: string; height_in: number; total_height_in: number; keep_together: boolean; prunable: boolean; match_score: number; item_path: string; children?: Array<{ type: string; height_in: number; total_height_in: number; prunable: boolean; match_score: number; item_path: string }> }>;
    sections_found: string[];
    constants_used: Record<string, number>;
  }> {
    const response = await this.client.post('/admin/pdf-calibration/simulate', params);
    return response.data;
  }

  // Admin - Prompt Management
  async listPrompts(filters?: { prompt_name?: string; prompt_type?: string; prompt_state?: string }): Promise<{ prompts: any[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.prompt_name) params.append('prompt_name', filters.prompt_name);
    if (filters?.prompt_type) params.append('prompt_type', filters.prompt_type);
    if (filters?.prompt_state) params.append('prompt_state', filters.prompt_state);
    const response = await this.client.get(`/admin/prompts?${params.toString()}`);
    return response.data;
  }

  async getPromptVersions(promptName: string): Promise<{ prompts: any[] }> {
    const response = await this.client.get(`/admin/prompts/${encodeURIComponent(promptName)}`);
    return response.data;
  }

  async updatePrompt(promptName: string, data: Record<string, any>): Promise<any> {
    const response = await this.client.put(`/admin/prompts/${encodeURIComponent(promptName)}`, data);
    return response.data;
  }

  async deletePrompt(promptName: string): Promise<any> {
    const response = await this.client.delete(`/admin/prompts/${encodeURIComponent(promptName)}`);
    return response.data;
  }

  async deletePromptVersion(promptName: string, promptVersion: string): Promise<any> {
    const response = await this.client.delete(`/admin/prompts/${encodeURIComponent(promptName)}?prompt_version=${encodeURIComponent(promptVersion)}`);
    return response.data;
  }

  async seedPrompts(): Promise<any> {
    const response = await this.client.post('/admin/prompts/seed');
    return response.data;
  }

  async clearPromptCache(): Promise<{ message: string; timestamp: number }> {
    const response = await this.client.post('/admin/prompts/cache-clear');
    return response.data;
  }

  async getLogConfig(): Promise<{ config: { default_level: string; overrides: Record<string, string> }; functions: string[] }> {
    const response = await this.client.get('/admin/logs/config');
    return response.data;
  }

  async updateLogConfig(config: { default_level: string; overrides: Record<string, string> }): Promise<any> {
    const response = await this.client.put('/admin/logs/config', config);
    return response.data;
  }

  async queryLogs(params: Record<string, string | number>): Promise<{ logs: any[] }> {
    const response = await this.client.get('/admin/logs', { params });
    return response.data;
  }

  // Admin - Pricing
  async getAdminPricing(): Promise<{ pricing: PricingConfig; updated_at: string; updated_by: string }> {
    const response = await this.client.get('/admin/pricing');
    return response.data;
  }

  async updateAdminPricing(pricing: PricingConfig): Promise<{ message: string; pricing: PricingConfig }> {
    const response = await this.client.put('/admin/pricing', { pricing });
    return response.data;
  }

  // Admin - Global Settings
  async getAdminGlobalSettings(): Promise<{ settings: Array<{ setting_key: string; setting_value: any; updated_at?: string; updated_by?: string }> }> {
    const response = await this.client.get('/admin/global-settings');
    return response.data;
  }

  async updateAdminGlobalSetting(setting_key: string, setting_value: any): Promise<{ message: string; setting: any }> {
    const response = await this.client.put('/admin/global-settings', { setting_key, setting_value });
    return response.data;
  }

  async deleteAdminGlobalSetting(key: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/global-settings/${encodeURIComponent(key)}`);
    return response.data;
  }

  // Public pricing (no auth required)
  async getPublicPricing(): Promise<{ pricing: PricingConfig }> {
    const response = await this.client.get('/pricing');
    return response.data;
  }

  // Public setting by key (no auth required, whitelisted keys only)
  async getPublicSetting(key: string): Promise<{ setting_key: string; setting_value: any }> {
    const response = await this.client.get(`/settings/${encodeURIComponent(key)}`);
    return response.data;
  }

  async lookupUser(query: string): Promise<{ userid: string }> {
    const response = await this.client.get('/admin/users', { params: { search: query, limit: 1 } });
    const users = response.data?.users || [];
    if (users.length > 0) {
      return { userid: users[0].userid };
    }
    throw new Error('User not found');
  }
}

// Final files generation response type
export interface GenerateFinalFilesResponse {
  message: string;
  files: {
    resume_html: string | null;
    resume_pdf: string | null;
    resume_txt: string | null;
    resume_template: string | null;
    cover_letter_html: string | null;
    cover_letter_pdf: string | null;
    cover_letter_txt: string | null;
  };
  s3_locations: {
    resume_html: string | null;
    resume_pdf: string | null;
    resume_txt: string | null;
    resume_template: string | null;
    cover_letter_html: string | null;
    cover_letter_pdf: string | null;
    cover_letter_txt: string | null;
  };
  phase: string;
}

// Pricing types
export interface PricingPlan {
  name: string;
  slug: string;
  tagline: string;
  display_price: string;
  price_cents: number;
  period: string;
  period_label: string;
  color: string;
  mono_label: string;
  cta_label: string;
  cta_url: string;
  cta_style: string;
  badge: string;
  badge_note?: string;
  features: string[];
  paused_features: string[];
  stripe_price_id?: string;
  sort_order: number;
  limits: {
    max_resumes?: number;
    monthly_jobs?: number;
    ai_enabled?: boolean;
  };
}

export interface PricingFaq {
  question: string;
  answer: string;
}

export interface PricingCssClass {
  class: string;
  description: string;
  example: string;
}

export interface PricingConfig {
  plans: Record<string, PricingPlan>;
  faq?: PricingFaq[];
  footer_note?: string;
  css_classes?: PricingCssClass[];
}

// Admin metrics types
export interface DynamoDBTableMetrics {
  item_count: number;
  size_bytes: number;
  status: string;
  error?: string;
}

export interface S3BucketMetrics {
  size_bytes: number;
  object_count: number;
  size_mb: number;
  error?: string;
}

export interface UserMetrics {
  total: number;
  by_role: Record<string, number>;
  by_status: Record<string, number>;
  by_plan: Record<string, number>;
  new_users_7d: number;
  new_users_30d: number;
  monthly_revenue?: number;
  annual_revenue_projected?: number;
  error?: string;
}

export interface CostEstimate {
  note: string;
  dynamodb: number;
  s3: number;
  lambda: number;
  api_gateway: number;
  cloudfront: number;
  cognito: number;
  bedrock: number;
  total_estimate: number;
  currency: string;
}

export interface GrowthTrends {
  total_jobs: number;
  new_jobs_7d: number;
  new_jobs_30d: number;
  total_resumes: number;
  error?: string;
}

export interface AdminMetricsResponse {
  timestamp: string;
  dynamodb: Record<string, DynamoDBTableMetrics>;
  s3: Record<string, S3BucketMetrics>;
  users: UserMetrics;
  cost_estimate: CostEstimate;
  growth: GrowthTrends;
}

// Model Assignment record from API (superset - includes function assignments and _cost_config)
export interface ModelAssignmentRecord {
  function_id: string;
  model_id?: string;
  description?: string;
  ai_pct?: number;
  token_in_formula?: string;
  token_out_formula?: string;
  // _cost_config fields
  job_desc?: number;
  resume?: number;
  small_section?: number;
  experience_section?: number;
  resume_tags?: number;
  job_tags?: number;
  jobs_per_month?: number;
  post_processors?: Array<{ fn: string; params: Record<string, unknown> }>;
  [key: string]: unknown;
}

// AI Metrics types
export interface MetricDatapoint {
  timestamp: string;
  count: number;
}

export interface AiMetricsResponse {
  invocations: Record<string, MetricDatapoint[]>;
  errors: Record<string, MetricDatapoint[]>;
  bedrock_cost_30d: number | null;
  timestamp: string;
}

// AI Usage tracking types
export interface AiUsageDailyPoint {
  date: string;
  calls: number;
  failures?: number;
  users: number;
}

export interface AiUsageModelData {
  total_calls: number;
  avg_calls_per_day: number;
  active_users: number;
  daily: AiUsageDailyPoint[];
}

export interface AiUsagePromptData {
  total_calls: number;
  avg_calls_per_day: number;
  total_failures: number;
  avg_failures_per_day: number;
  active_users: number;
  daily: AiUsageDailyPoint[];
}

export interface AiUsageResponse {
  models: Record<string, AiUsageModelData>;
  prompts: Record<string, AiUsagePromptData>;
  start_date: string;
  end_date: string;
  days_in_range: number;
}

// Admin User Detail types
export interface AdminUserJobSummary {
  jobid: string;
  jobphase: string;
  jobtitle?: string;
  jobcompany?: string;
  createdAt?: string;
}

export interface AdminUserResumeSummary {
  resumename: string;
  lastupdate?: string;
}

export interface AdminUserDetail {
  user: Record<string, string | number | boolean | undefined>;
  job_count: number;
  resume_count: number;
  jobs_summary: AdminUserJobSummary[];
  resumes_summary: AdminUserResumeSummary[];
}

// Admin User List types
export interface AdminUserListItem {
  userid: string;
  email: string;
  userhandle: string;
  role: string;
  status: string;
  plan: string;
  createdAt: string;
  org_id?: string;
}

// Marketing data types (API response shapes)
export interface MarketingLinkData {
  label: string;
  url: string;
}

export interface MarketingTaskData {
  item_id: string;
  item_type: 'task';
  parent_id: string;
  title: string;
  description: string;
  status: string;
  due_date: string;
  execution_type: string;
  priority: string;
  links: MarketingLinkData[];
  notes: string;
  sort_order: number;
}

export interface MarketingObjectiveData {
  item_id: string;
  item_type: 'objective';
  parent_id: string;
  title: string;
  description: string;
  status: string;
  due_date: string;
  execution_type: string;
  tasks: MarketingTaskData[];
  sort_order: number;
}

export interface MarketingCampaignData {
  item_id: string;
  item_type: 'campaign';
  title: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
  target_metrics: string;
  objectives: MarketingObjectiveData[];
  total_tasks: number;
  completed_tasks: number;
  percent_complete: number;
  sort_order: number;
}

// Marketing types
export interface MarketingItemPayload {
  item_type: string;
  parent_id?: string;
  title: string;
  description?: string;
  due_date?: string;
  priority?: string;
  execution_type?: string;
  status?: string;
  [key: string]: string | undefined;
}

// Work Queue types
export interface WorkQueueItem {
  request_id: string;
  created_at: string;
  status: string;
  target_area: string;
  target_component: string;
  request_text: string;
  completed_at: string;
  result_notes: string;
  created_by: string;
}

export const api = new ApiService();
